/* eslint-disable import/no-unresolved */
import React, {
  useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import { useForm } from 'react-hook-form';

import RightModalLayout from '../../../../../../../../layouts/Modal/RightModalLayout';
import CardMC from '../../../../../../../../components/CardMC';
import IconMC from '../../../../../../../../components/IconMC';
import ButtonMC from '../../../../../../../../components/ButtonMC';
import InputComponent from '../../../../../../../../components/InputMC';
import FormProviderMC from '../../../../../../../../components/FormProviderMC';
import { showtoast } from '../../../../../../../../../util/toast';
import PreviewPdfInstruction from '../../../../../Guides/PreviewPdfInstruction';
import PdfPreviewOrIcon from '../../../../../Config/PdfPreviewOrIcon';
import { isAllowedMediaUpload, storedMediaKind, uploadFileKind } from '../../../../../Config/mediaUploadFile';

const SendFileMediaThumb = ({ item, checked }) => {
  const kind = storedMediaKind(item);
  const iconColor = checked ? 'tw-text-blue-500' : 'tw-text-gray-500';
  const border = checked ? 'tw-border-blue-200' : 'tw-border-gray-200';

  if (kind === 'image') {
    return (
      <div className={`tw-w-8 tw-h-8 tw-rounded-lg tw-overflow-hidden tw-shrink-0 tw-border ${checked ? 'tw-border-blue-200 tw-bg-blue-50' : 'tw-border-gray-200 tw-bg-gray-100'}`}>
        <img src={item.url} alt="" className="tw-w-full tw-h-full tw-object-cover" />
      </div>
    );
  }

  if (kind === 'video') {
    return (
      <div className={`tw-w-8 tw-h-8 tw-rounded-lg tw-overflow-hidden tw-shrink-0 tw-border tw-bg-black ${border}`}>
        <video
          src={item.url}
          className="tw-w-full tw-h-full tw-object-cover"
          muted
          playsInline
          preload="metadata"
          onLoadedData={(event) => {
            const video = event.currentTarget;
            if (video.currentTime < 0.1) video.currentTime = 0.1;
          }}
        />
      </div>
    );
  }

  if (kind === 'pdf') {
    return (
      <PdfPreviewOrIcon
        src={item.url}
        iconClassName={iconColor}
        wrapperClassName={`tw-w-8 tw-h-8 tw-rounded-lg tw-overflow-hidden tw-shrink-0 tw-border ${checked ? 'tw-border-blue-200 tw-bg-blue-50' : 'tw-border-gray-200 tw-bg-gray-100'}`}
        iconWrapperClassName={`tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-shrink-0 ${checked ? 'tw-bg-blue-50' : 'tw-bg-gray-50'}`}
      />
    );
  }

  return (
    <div className={`tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-shrink-0 ${checked ? 'tw-bg-blue-50' : 'tw-bg-gray-50'}`}>
      <IconMC name="new-document" className={iconColor} />
    </div>
  );
};

const SendFileToolView = ({
  t,
  footerSubmitLabel,
  handleBack,
  handleConfirmSendFile,
  mediaItems,
  mediaLoading,
  selectedFileIds,
  toggleFileId,
  uploadingMedia,
  uploadNewMedia
}) => {
  const fileInputRef = useRef(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState('');
  const [pdfPreviewFailed, setPdfPreviewFailed] = useState(false);
  const sizeLimitBytes = 10 * 1024 * 1024;
  const uploadFormMethods = useForm({ defaultValues: { tool_send_file_reference_name: '' } });

  const uploadImageObjectUrl = useMemo(() => {
    if (!uploadFile?.type?.startsWith('image/')) return null;
    return URL.createObjectURL(uploadFile);
  }, [uploadFile]);

  const uploadVideoObjectUrl = useMemo(() => {
    if (uploadFileKind(uploadFile) !== 'video') return null;
    return URL.createObjectURL(uploadFile);
  }, [uploadFile]);

  const uploadPdfObjectUrl = useMemo(() => {
    if (uploadFileKind(uploadFile) !== 'pdf') return null;
    return URL.createObjectURL(uploadFile);
  }, [uploadFile]);

  useEffect(() => () => {
    if (uploadImageObjectUrl) URL.revokeObjectURL(uploadImageObjectUrl);
  }, [uploadImageObjectUrl]);

  useEffect(() => () => {
    if (uploadVideoObjectUrl) URL.revokeObjectURL(uploadVideoObjectUrl);
  }, [uploadVideoObjectUrl]);

  useEffect(() => () => {
    if (uploadPdfObjectUrl) URL.revokeObjectURL(uploadPdfObjectUrl);
  }, [uploadPdfObjectUrl]);

  const selectedCount = selectedFileIds?.length || 0;
  const canAddMore = selectedCount < 5;

  const handlePdfPreviewError = useCallback(() => setPdfPreviewFailed(true), []);
  const clearUploadSelection = () => {
    setUploadFile(null);
    setUploadName('');
    setPdfPreviewFailed(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const onPickUpload = () => fileInputRef.current?.click?.();
  const onUploadFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;
    if (!isAllowedMediaUpload(file)) {
      showtoast(t('general.invalid_file_type'));
      e.target.value = '';
      return;
    }
    if (file.size > sizeLimitBytes) {
      showtoast(t('general.file_too_large'));
      e.target.value = '';
      return;
    }
    setPdfPreviewFailed(false);
    setUploadFile(file);
    setUploadName(file.name);
  };

  const onUploadSave = async () => {
    if (!uploadFile || !uploadName.trim()) return;
    if (!canAddMore) return;
    try {
      await uploadNewMedia(uploadFile, uploadName.trim());
      clearUploadSelection();
    } catch {
      showtoast(t('general.save_error'));
    }
  };

  const mediaList = useMemo(() => mediaItems || [], [mediaItems]);

  return (
    <RightModalLayout
      closeAction={handleBack}
      title={t('tools.send_file_title')}
      showBackButton
      submitLabel={footerSubmitLabel}
      submitAction={handleConfirmSendFile}
      cancelLabel={t('Common:actions.cancel')}
      cancelAction={handleBack}
      footerVariant="VERTICAL"
    >
      <div className="tw-flex tw-flex-col tw-gap-3">
        <div className="tw-text-xs tw-text-gray-600">
          {t('tools.send_file_picker_help', { count: 5 })}
        </div>

        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
          <div className="tw-text-xs tw-text-gray-500">
            {t('tools.selected_count', { count: selectedCount })}
          </div>

          <ButtonMC
            text={t('tools.send_file_upload_new')}
            variant="SECONDARY"
            outlined
            size="small"
            onClick={onPickUpload}
            disabled={!canAddMore || uploadingMedia}
            className="tw-px-4 tw-py-1"
          />
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf,video/mp4,video/3gpp,.mp4,.3gp"
          className="tw-hidden"
          onChange={onUploadFileChange}
        />

        {uploadFile && (
          <CardMC className="tw-w-full tw-flex-col tw-p-3">
            <FormProviderMC methods={uploadFormMethods} className="tw-w-full tw-min-w-0">
              <div className="tw-flex tw-w-full tw-min-w-0 tw-flex-col tw-gap-3">
                {uploadImageObjectUrl && (
                  <div className="tw-w-full tw-rounded-lg tw-overflow-hidden tw-border tw-border-gray-200 tw-bg-gray-50 tw-max-h-48">
                    <img
                      src={uploadImageObjectUrl}
                      alt={uploadFile.name || ''}
                      className="tw-w-full tw-max-h-48 tw-object-contain"
                    />
                  </div>
                )}
                {uploadVideoObjectUrl && (
                  <div className="tw-w-full tw-rounded-lg tw-overflow-hidden tw-border tw-border-gray-200 tw-bg-black tw-max-h-48">
                    <video
                      src={uploadVideoObjectUrl}
                      className="tw-w-full tw-max-h-48 tw-object-contain"
                      controls
                      playsInline
                      preload="metadata"
                    />
                  </div>
                )}
                {uploadPdfObjectUrl && !pdfPreviewFailed && (
                  <div className="tw-w-full tw-rounded-lg tw-overflow-hidden tw-border tw-border-gray-200 tw-bg-gray-50 tw-max-h-48 tw-flex tw-justify-center">
                    <PreviewPdfInstruction
                      loadedFile={uploadPdfObjectUrl}
                      imageClassName="tw-w-full tw-max-h-48 tw-object-contain"
                      onError={handlePdfPreviewError}
                    />
                  </div>
                )}
                {uploadPdfObjectUrl && pdfPreviewFailed && (
                  <div className="tw-w-full tw-h-24 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-50 tw-flex tw-items-center tw-justify-center">
                    <IconMC name="new-document" className="tw-text-gray-500" />
                  </div>
                )}
                <div className="tw-text-xs tw-text-gray-600">
                  {t('tools.send_file_upload_hint')}
                </div>
                <InputComponent
                  id="tool_send_file_reference_name"
                  name="tool_send_file_reference_name"
                  value={uploadName}
                  label={t('tools.send_file_reference_name_label')}
                  placeholder={t('tools.send_file_reference_name_placeholder')}
                  onChange={(_, v) => setUploadName(v)}
                  useFloatingLabel
                  containerClass="tw-w-full tw-min-w-0"
                />
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
                  <ButtonMC
                    text={t('Common:actions.cancel')}
                    variant="SECONDARY"
                    onClick={clearUploadSelection}
                    disabled={uploadingMedia}
                    className="!tw-px-3"
                  />
                  <ButtonMC
                    text={t('Common:actions.save')}
                    onClick={onUploadSave}
                    disabled={uploadingMedia || !uploadName.trim()}
                    className="!tw-px-3"
                  />
                </div>
              </div>
            </FormProviderMC>
          </CardMC>
        )}

        <div className="tw-flex tw-flex-col tw-gap-2 tw-mt-2">
          {mediaList.map((m) => {
            const checked = selectedFileIds?.includes?.(String(m.id));
            return (
              <CardMC
                key={m.id}
                className={`tw-p-3 tw-flex tw-items-center tw-justify-between tw-cursor-pointer hover:tw-bg-gray-50 ${
                  checked ? 'tw-bg-gray-50 !tw-border-gray-300' : ''
                }`}
                onClick={() => toggleFileId(String(m.id))}
              >
                <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0">
                  <SendFileMediaThumb item={m} checked={checked} />
                  <div className="tw-min-w-0">
                    <div className="tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate">
                      {m.name || m.original_name || m.id}
                    </div>
                    <div className="tw-text-[11px] tw-text-gray-500 tw-font-mono tw-truncate">
                      {m.id}
                    </div>
                  </div>
                </div>

                <div className="tw-flex tw-items-center tw-gap-2 tw-shrink-0">
                  {checked ? (
                    <IconMC name="checkmark-circle-2-fill" className="tw-text-blue-500" />
                  ) : (
                    <IconMC name="circle-outline" className="tw-text-gray-300" />
                  )}
                </div>
              </CardMC>
            );
          })}

          {!mediaLoading && (!mediaItems || mediaItems.length === 0) && (
            <div className="tw-text-xs tw-text-gray-500">
              {t('tools.send_file_empty')}
            </div>
          )}
        </div>
      </div>
    </RightModalLayout>
  );
};

export default SendFileToolView;
